import React, { useEffect, useRef, useState } from 'react';
import type { Settings } from '../types';
import { playAudio, SOUND_PRESETS } from '../utils/audioUtils.ts';
import './SettingsModal.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: Settings;
  onSettingsChange: (settings: Settings) => void;
}

const DURATION_FIELDS = ['workDuration', 'shortBreakDuration', 'longBreakDuration', 'longBreakInterval'];

const BG_PRESETS = [
  // design-system: intentional override — raw hex used to render colour swatches; cannot reference vars
  { id: 'warm-stone', name: 'Warm Stone', bg: '#e8e5dd' }, // design-system: intentional override
  { id: 'bone',       name: 'Bone',       bg: '#efeae0' }, // design-system: intentional override
  { id: 'mist',       name: 'Mist',       bg: '#e6e8eb' }, // design-system: intentional override
  { id: 'sand',       name: 'Sand',       bg: '#e9dfc9' }, // design-system: intentional override
  { id: 'slate',      name: 'Slate',      bg: '#d8dde2' }, // design-system: intentional override
  { id: 'paper',      name: 'Paper',      bg: '#f4f3ef' }, // design-system: intentional override
  { id: 'sage',       name: 'Sage',       bg: '#dfe3d9' }, // design-system: intentional override
  { id: 'blush',      name: 'Blush',      bg: '#ecdfdb' }, // design-system: intentional override
];

const validateDurationField = (key: string, value: number): string => {
  if (!Number.isFinite(value)) return 'Must be a number';
  if (key === 'longBreakInterval') {
    if (value < 1) return 'Minimum 1';
    if (value > 10) return 'Maximum 10';
  } else {
    if (value < 1) return 'Minimum 1 minute';
    if (value > 180) return 'Maximum 180 minutes';
  }
  return '';
};

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const openSnapshotRef = useRef<string>('');

  useEffect(() => {
    setLocalSettings(settings);
    setValidationErrors({});
    if (isOpen) openSnapshotRef.current = JSON.stringify(settings);
  }, [settings, isOpen]);

  const handleChange = (key: string, value: any) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
    if (DURATION_FIELDS.includes(key)) {
      const err = validateDurationField(key, value);
      setValidationErrors(prev => {
        const next = { ...prev };
        if (err) next[key] = err; else delete next[key];
        return next;
      });
    }
  };

  const handleVolumeChange = (volume: number) => {
    handleChange('volume', volume);
    // Preview the volume with work complete sound
    if (localSettings.soundEnabled && localSettings.workCompleteSound) {
      playAudio(localSettings.workCompleteSound, volume);
    }
  };

  const handleSave = () => {
    if (Object.keys(validationErrors).length > 0) return;
    onSettingsChange(localSettings);
    if (typeof window.gtag !== 'undefined' && JSON.stringify(localSettings) !== openSnapshotRef.current) {
      window.gtag('event', 'settings_saved', { event_category: 'pomodoro_timer' });
    }
    onClose();
  };

  const handleCancel = () => {
    setLocalSettings(settings);
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const isDark = localSettings.theme === 'dark';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="settings-section">
            <h3>Timer Settings</h3>
            <div className="setting-item">
              <label>Work Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="180"
                value={localSettings.workDuration}
                className={validationErrors.workDuration ? 'input-error' : undefined}
                onChange={(e) => handleChange('workDuration', parseInt(e.target.value))}
              />
              {validationErrors.workDuration && (
                <span className="error-message">{validationErrors.workDuration}</span>
              )}
            </div>
            <div className="setting-item">
              <label>Short Break Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="180"
                value={localSettings.shortBreakDuration}
                className={validationErrors.shortBreakDuration ? 'input-error' : undefined}
                onChange={(e) => handleChange('shortBreakDuration', parseInt(e.target.value))}
              />
              {validationErrors.shortBreakDuration && (
                <span className="error-message">{validationErrors.shortBreakDuration}</span>
              )}
            </div>
            <div className="setting-item">
              <label>Long Break Duration (minutes)</label>
              <input
                type="number"
                min="1"
                max="180"
                value={localSettings.longBreakDuration}
                className={validationErrors.longBreakDuration ? 'input-error' : undefined}
                onChange={(e) => handleChange('longBreakDuration', parseInt(e.target.value))}
              />
              {validationErrors.longBreakDuration && (
                <span className="error-message">{validationErrors.longBreakDuration}</span>
              )}
            </div>
            <div className="setting-item">
              <label>Long Break Interval (pomodoros)</label>
              <input
                type="number"
                min="1"
                max="10"
                value={localSettings.longBreakInterval}
                className={validationErrors.longBreakInterval ? 'input-error' : undefined}
                onChange={(e) => handleChange('longBreakInterval', parseInt(e.target.value))}
              />
              {validationErrors.longBreakInterval && (
                <span className="error-message">{validationErrors.longBreakInterval}</span>
              )}
            </div>
          </div>

          <div className="settings-section">
            <h3>Auto-Start</h3>
            <div className="setting-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={localSettings.autoStartBreaks}
                  onChange={(e) => handleChange('autoStartBreaks', e.target.checked)}
                />
                Auto-start breaks
              </label>
            </div>
            <div className="setting-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={localSettings.autoStartWork}
                  onChange={(e) => handleChange('autoStartWork', e.target.checked)}
                />
                Auto-start work sessions
              </label>
            </div>
          </div>

          <div className="settings-section">
            <h3>Sound</h3>
            <div className="setting-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={localSettings.soundEnabled}
                  onChange={(e) => handleChange('soundEnabled', e.target.checked)}
                />
                Enable sound notifications
              </label>
            </div>
            <div className="setting-item">
              <label>Volume</label>
              <input
                type="range"
                min="0"
                max="100"
                value={localSettings.volume}
                onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                disabled={!localSettings.soundEnabled}
              />
              <span className="volume-display">{localSettings.volume}%</span>
            </div>
            <div className="setting-item">
              <label>Work Session Complete Sound</label>
              <div className="sound-picker">
                <select
                  value={localSettings.workCompleteSound}
                  onChange={(e) => handleChange('workCompleteSound', e.target.value)}
                  disabled={!localSettings.soundEnabled}
                >
                  {SOUND_PRESETS.map(p => (
                    <option key={p.path} value={p.path}>{p.label}</option>
                  ))}
                </select>
                <button
                  className="preview-btn"
                  disabled={!localSettings.soundEnabled || !localSettings.workCompleteSound}
                  onClick={() => playAudio(localSettings.workCompleteSound, localSettings.volume)}
                >
                  Preview
                </button>
              </div>
            </div>
            <div className="setting-item">
              <label>Break Complete Sound</label>
              <div className="sound-picker">
                <select
                  value={localSettings.breakCompleteSound}
                  onChange={(e) => handleChange('breakCompleteSound', e.target.value)}
                  disabled={!localSettings.soundEnabled}
                >
                  {SOUND_PRESETS.map(p => (
                    <option key={p.path} value={p.path}>{p.label}</option>
                  ))}
                </select>
                <button
                  className="preview-btn"
                  disabled={!localSettings.soundEnabled || !localSettings.breakCompleteSound}
                  onClick={() => playAudio(localSettings.breakCompleteSound, localSettings.volume)}
                >
                  Preview
                </button>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Appearance</h3>

            {/* Theme toggle — 3-way: Light / System / Dark */}
            <div className="setting-item">
              <label>Theme</label>
              <div className="segmented-control">
                {(['light', 'system', 'dark'] as const).map(opt => (
                  <button
                    key={opt}
                    className={`segmented-btn${localSettings.theme === opt ? ' segmented-btn--active' : ''}`}
                    onClick={() => handleChange('theme', opt)}
                  >
                    {opt === 'light' ? 'Light' : opt === 'system' ? 'System' : 'Dark'}
                  </button>
                ))}
              </div>
            </div>

            {/* Density toggle — 2-way: Cozy / Compact */}
            <div className="setting-item">
              <label>Density</label>
              <div className="segmented-control">
                {(['cozy', 'compact'] as const).map(opt => (
                  <button
                    key={opt}
                    className={`segmented-btn${localSettings.density === opt ? ' segmented-btn--active' : ''}`}
                    onClick={() => handleChange('density', opt)}
                  >
                    {opt === 'cozy' ? 'Cozy' : 'Compact'}
                  </button>
                ))}
              </div>
            </div>

            {/* Colour palette picker — Focus (blue) / Library (green) */}
            <div className="setting-item">
              <label>Colour Palette</label>
              <div className="segmented-control">
                {(['default', 'library'] as const).map(opt => (
                  <button
                    key={opt}
                    className={`segmented-btn${localSettings.accent === opt ? ' segmented-btn--active' : ''}`}
                    onClick={() => handleChange('accent', opt)}
                  >
                    {opt === 'default' ? 'Focus (blue)' : 'Library (green)'}
                  </button>
                ))}
              </div>
            </div>

            {/* Background preset picker — 8 swatches; disabled in dark mode */}
            <div className={`setting-item${isDark ? ' setting-item--dimmed' : ''}`}>
              <label>Background Preset{isDark ? ' (unavailable in dark mode)' : ''}</label>
              <div className="bg-preset-grid">
                {BG_PRESETS.map(preset => (
                  <button
                    key={preset.id}
                    className={`bg-preset-swatch${localSettings.bgPreset === preset.id ? ' bg-preset-swatch--active' : ''}`}
                    style={{ backgroundColor: preset.bg }}
                    title={preset.name}
                    disabled={isDark}
                    onClick={() => handleChange('bgPreset', preset.id)}
                    aria-label={preset.name}
                  >
                    <span className="bg-preset-label">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h3>Keyboard Shortcuts</h3>
            <table className="shortcuts-table">
              <tbody>
                <tr><td><kbd>Space</kbd></td><td>Start / Pause</td></tr>
                <tr><td><kbd>S</kbd></td><td>Skip session</td></tr>
                <tr><td><kbd>R</kbd></td><td>Reset timer</td></tr>
                <tr><td><kbd>T</kbd></td><td>Toggle task panel</td></tr>
                <tr><td><kbd>M</kbd></td><td>Toggle mini timer</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={Object.keys(validationErrors).length > 0}
          >
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
